import {
  type ShareUrlCopiedSettingsParams,
  type ShareUrlGeneratedObjectType,
  type ShareUrlGeneratedVersion,
} from '@air/analytics';
import { type ShortUrlResponse } from '@air/api/types';

import { getDownloadPermissionMode } from '~/utils/shortUrl/buildDownloadPermissionShortUrlUpdate';
import { buildShareUrlGeneratedAnalyticsParams } from '~/utils/shortUrl/buildShareUrlGeneratedAnalyticsParams';
import { getShareUrlGeneratedVersionFromShortUrl } from '~/utils/shortUrl/getShareUrlGeneratedVersion';
import {
  downloadPermissionModeToAnalytics,
  versionControlToAnalytics,
} from '~/utils/shortUrl/shareUrlUpdatedAnalytics';

type ShortUrlForShareUrlCopiedAnalytics = Pick<ShortUrlResponse, 'presentationSettings' | 'permissions'> & {
  version?: number;
};

export type BuildShareUrlCopiedAnalyticsParamsInput = {
  shareId: string;
  shortUrl: ShortUrlForShareUrlCopiedAnalytics;
  objectType: ShareUrlGeneratedObjectType;
  version?: ShareUrlGeneratedVersion;
};

/**
 * Build the settings snapshot sent with the `Copied a share url` event.
 *
 * Reuses the generate-event builder for the shared 6 properties and adds the two
 * V2-modal settings (`downloads`, `version_control`) derived from the same
 * persisted `(canViewOriginal, hideDownloadAll)` / `canViewAllVersions` state the
 * modal reads. `version_control` is variant-sensitive: a single clip reports
 * `this_version` when not showing all versions, whereas MASHAs report `latest_only`.
 */
export const buildShareUrlCopiedAnalyticsParams = ({
  shareId,
  shortUrl,
  objectType,
  version,
}: BuildShareUrlCopiedAnalyticsParamsInput): ShareUrlCopiedSettingsParams => {
  const generated = (() => {
    if (objectType === 'asset' || objectType === 'clip') {
      return buildShareUrlGeneratedAnalyticsParams({
        shareId,
        shortUrl,
        objectType,
        version: version ?? getShareUrlGeneratedVersionFromShortUrl(shortUrl, objectType),
      });
    }

    return buildShareUrlGeneratedAnalyticsParams({ shareId, shortUrl, objectType });
  })();

  const downloadMode = getDownloadPermissionMode(
    shortUrl.permissions.asset.canViewOriginal,
    shortUrl.presentationSettings?.hideDownloadAll,
  );

  const base: ShareUrlCopiedSettingsParams = {
    metadata: generated.metadata,
    custom_fields: generated.custom_fields,
    comments: generated.comments,
    downloads: downloadPermissionModeToAnalytics(downloadMode),
    version_control: versionControlToAnalytics(
      shortUrl.permissions.asset.canViewAllVersions,
      objectType === 'clip' ? 'single-asset' : 'multi-asset',
    ),
  };

  if ('hide_search_input' in generated) {
    return {
      ...base,
      hide_search_input: generated.hide_search_input,
      disable_view_settings: generated.disable_view_settings,
      disable_expanding_nested: generated.disable_expanding_nested,
    };
  }

  return base;
};
